import moment from 'moment-timezone';

const GOOGLE_MAPS_SEARCH_URL = 'https://www.google.com/maps/search/';

const isObject = (value) =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

const normalizeId = (value) => String(value ?? '').trim();
const UUID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUuidLike = (value) => UUID_PATTERN.test(normalizeId(value));

const addUniqueValue = (values, value) => {
    const normalized = normalizeId(value);
    if (normalized && !values.includes(normalized)) {
        values.push(normalized);
    }
};

const addLookupValue = (lookup, value, screen) => {
    const key = normalizeId(value).toLowerCase();
    if (key) {
        lookup.set(key, screen);
    }
};

const buildFetchedScreenLookup = (screens = []) => {
    const lookup = new Map();

    screens.forEach((screen) => {
        if (!isObject(screen)) return;
        addLookupValue(lookup, screen.id, screen);
        addLookupValue(lookup, screen.external_id, screen);
        addLookupValue(lookup, screen.externalId, screen);
        addLookupValue(lookup, screen.alias, screen);
        addLookupValue(lookup, screen.name, screen);
    });

    return lookup;
};

const getScreenLookupId = (screen) => {
    if (isObject(screen)) {
        return normalizeId(screen.external_id || screen.externalId || screen.id);
    }

    return normalizeId(screen);
};

const getScreenUuid = (...screens) => {
    for (const screen of screens) {
        if (!isObject(screen)) continue;

        const candidates = [
            screen.uuid,
            screen.screen_uuid,
            screen.screenUuid,
            screen.internal_id,
            screen.internalId,
            screen.id,
        ];

        const uuid = candidates.find(isUuidLike);
        if (uuid) return normalizeId(uuid);
    }

    return '';
};

export const getCampaignAuditLookupIds = (screens = []) =>
    Array.from(
        new Set(screens.map(getScreenLookupId).filter(Boolean))
    );

export const getPrimaryScreenImage = (screen = {}) => {
    if (!isObject(screen)) return '';

    if (screen.image?.url) return screen.image.url;
    if (screen.image_url) return screen.image_url;
    if (screen.url) return screen.url;

    if (!Array.isArray(screen.gallery)) return '';

    for (const gallery of screen.gallery) {
        const items = Array.isArray(gallery?.gallery_items)
            ? gallery.gallery_items
            : [];
        const item = items.find((galleryItem) => galleryItem?.image?.url);
        if (item?.image?.url) return item.image.url;
    }

    return '';
};

export const toInlineScreenImageUrl = (url) => {
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}response-content-disposition=inline`;
};

export const getAuditScreenCoordinates = (screen = {}) => {
    const lat = Number(screen?.geo?.lat ?? screen?.lat);
    const lng = Number(screen?.geo?.lon ?? screen?.geo?.lng ?? screen?.lng);

    return {
        lat: Number.isFinite(lat) ? lat : null,
        lng: Number.isFinite(lng) ? lng : null,
    };
};

const getAuditScreenLocation = (screen = {}) => {
    if (screen?.geo?.name) return screen.geo.name;
    if (screen?.location) return screen.location;

    const geoParts = [
        screen?.geo?.route,
        screen?.geo?.number,
        screen?.geo?.area1,
        screen?.geo?.locality2,
        screen?.geo?.country,
    ].filter(Boolean);

    return geoParts.join(', ');
};

const mergeScreens = (fetchedScreen, campaignScreen) => {
    if (!isObject(campaignScreen)) {
        return fetchedScreen || {
            id: campaignScreen,
            external_id: campaignScreen,
            name: campaignScreen,
        };
    }

    return {
        ...(fetchedScreen || {}),
        ...campaignScreen,
        geo: {
            ...(fetchedScreen?.geo || {}),
            ...(campaignScreen?.geo || {}),
        },
    };
};

export const normalizeCampaignAuditScreens = (
    campaignScreens = [],
    fetchedScreens = []
) => {
    const fetchedLookup = buildFetchedScreenLookup(fetchedScreens);
    const seen = new Set();

    return campaignScreens
        .map((screen, index) => {
            const lookupId = getScreenLookupId(screen);
            const fetchedScreen = fetchedLookup.get(lookupId.toLowerCase());
            const source = mergeScreens(fetchedScreen, screen);
            const auditUuid = getScreenUuid(source, fetchedScreen, screen);
            const auditId = normalizeId(
                source.external_id || source.externalId || source.id || lookupId
            );

            if (!auditId) return null;

            const dedupeKey = auditId.toLowerCase();
            if (seen.has(dedupeKey)) return null;
            seen.add(dedupeKey);

            const coordinates = getAuditScreenCoordinates(source);
            const name = normalizeId(
                source.name || source.alias || source.label || auditId
            );

            return {
                ...source,
                auditKey: `${auditId}-${index}`,
                auditId,
                auditUuid,
                auditName: name,
                auditLocation: getAuditScreenLocation(source),
                auditLat: coordinates.lat,
                auditLng: coordinates.lng,
                auditImageUrl: getPrimaryScreenImage(source),
            };
        })
        .filter(Boolean);
};

export const getCampaignAuditEndpointScreenId = (screen = {}) =>
    normalizeId(screen.auditUuid || (isUuidLike(screen.auditId) ? screen.auditId : ''));

export const getCampaignAuditAliases = (screen = {}) => {
    const aliases = [];

    [
        screen.auditId,
        screen.external_id,
        screen.externalId,
        screen.taggifyId,
        screen.tag,
        screen.alias,
    ].forEach((value) => addUniqueValue(aliases, value));

    const numericAliases = aliases
        .map((value) => value.replace(/^tgx_/i, ''))
        .filter((value) => /^\d+$/.test(value));

    numericAliases.forEach((value) => {
        addUniqueValue(aliases, value);
        addUniqueValue(aliases, `tgx_${value}`);
    });

    return aliases.filter((value) => value !== getCampaignAuditEndpointScreenId(screen));
};

export const shouldFetchScreenDetails = (screen) => {
    if (!isObject(screen)) return Boolean(getScreenLookupId(screen));

    const coordinates = getAuditScreenCoordinates(screen);
    return (
        Boolean(getScreenLookupId(screen)) &&
        (coordinates.lat === null ||
            coordinates.lng === null ||
            !getPrimaryScreenImage(screen) ||
            !screen.geo?.name)
    );
};

export const buildGoogleMapsUrl = (lat, lng) => {
    const parsedLat = Number(lat);
    const parsedLng = Number(lng);

    if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) {
        return '';
    }

    return `${GOOGLE_MAPS_SEARCH_URL}?api=1&query=${encodeURIComponent(
        `${parsedLat},${parsedLng}`
    )}`;
};

const getTimezoneOffsetMinutes = (timezone = {}) => {
    const rawOffset = timezone.gmt ?? timezone.displacement ?? 0;
    const normalizedOffset = String(rawOffset).replace(/^gmt/i, '').trim();
    const match = normalizedOffset.match(/^([+-])?(\d{1,2})(?::?(\d{2}))?$/);

    if (!match) return 0;

    const sign = match[1] === '-' ? -1 : 1;
    const hours = Number(match[2]);
    const minutes = Number(match[3] || 0);

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;

    return sign * (hours * 60 + minutes);
};

const getTimezoneLabel = (timezone = {}) => {
    if (timezone.description) return timezone.description;
    if (timezone.name) return timezone.name;
    if (timezone.gmt !== undefined && timezone.gmt !== null) {
        return `GMT${timezone.gmt}`;
    }
    return 'UTC';
};

export const getCampaignAuditHourRange = (timezone = {}, now = new Date()) => {
    const timezoneName = timezone?.name;
    const currentMoment =
        timezoneName && moment.tz.zone(timezoneName)
            ? moment(now).tz(timezoneName)
            : moment.utc(now).utcOffset(getTimezoneOffsetMinutes(timezone));
    const displayFrom = currentMoment.clone().startOf('hour');
    const displayTo = displayFrom.clone().add(1, 'hour');
    const utcFrom = displayFrom.clone().utc();
    const utcTo = displayTo.clone().utc();

    return {
        displayRange: `${displayFrom.format('HH:mm')} - ${displayTo.format(
            'HH:mm'
        )}`,
        timezoneLabel: getTimezoneLabel(timezone),
        utcFrom: utcFrom.format('YYYY-MM-DDTHH:mm:ss[Z]'),
        utcTo: utcTo.format('YYYY-MM-DDTHH:mm:ss[Z]'),
        utcHourKey: utcFrom.format('YYYYMMDDHH'),
    };
};

export const formatCampaignAuditRecordDate = (timestamp, timezone = {}) => {
    if (!timestamp) return '-';

    const utcMoment = moment.utc(timestamp);
    if (!utcMoment.isValid()) return '-';

    const timezoneName = timezone?.name;
    const displayMoment =
        timezoneName && moment.tz.zone(timezoneName)
            ? utcMoment.clone().tz(timezoneName)
            : utcMoment.clone().utcOffset(getTimezoneOffsetMinutes(timezone));

    return displayMoment.format('DD-MM-YYYY HH:mm');
};

const CREATIVE_DURATION_DECIMALS = 5;

// Exact duration with up to 5 decimals and no trailing zeros: 8.00003s, 15s.
export const formatCreativeDuration = (seconds) => {
    const value = Number(seconds);
    if (seconds === null || seconds === '' || !Number.isFinite(value) || value <= 0) {
        return '-';
    }

    return `${Number(value.toFixed(CREATIVE_DURATION_DECIMALS))}s`;
};

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm', 'm4v', 'avi', 'mkv'];

export const isVideoCreative = (creative) => {
    const mimeType = normalizeId(creative?.mimeType).toLowerCase();
    if (mimeType) return mimeType.startsWith('video/');

    return VIDEO_EXTENSIONS.includes(
        normalizeId(creative?.extension).toLowerCase()
    );
};

const HTML_MIME_TYPE = 'text/html';
const HTML_AD_TYPES = ['7', 'dynamic', 'html5'];
const HTML_EXTENSIONS = ['html', 'htm', 'zip'];

// HTML5 / dynamic creatives (ads_preview.type 7) cannot be previewed as an
// image: they are rendered from their url in an iframe.
export const isHtmlCreative = (creative) => {
    const mimeType = normalizeId(creative?.mimeType).toLowerCase();
    if (mimeType.startsWith(HTML_MIME_TYPE)) return true;

    if (HTML_AD_TYPES.includes(normalizeId(creative?.adType).toLowerCase())) {
        return true;
    }

    const urlPath = normalizeId(creative?.url).split(/[?#]/)[0].toLowerCase();
    if (/\.html?$/.test(urlPath)) return true;

    return HTML_EXTENSIONS.includes(
        normalizeId(creative?.extension).toLowerCase()
    );
};

export const getCreativeTypeLabel = (creative) => {
    const mimeType = normalizeId(creative?.mimeType);
    const extension = normalizeId(creative?.extension).toUpperCase();

    if (mimeType && extension) return `${mimeType} (${extension})`;
    return mimeType || extension || '-';
};
